<script lang="ts">
    import { Accordion, AccordionItem, Button, DatePicker, DatePickerInput, Modal, Select, SelectItem, StructuredList, StructuredListBody, StructuredListCell, StructuredListHead, StructuredListRow, StructuredListSkeleton, TextInput } from "carbon-components-svelte";
    import { onMount } from "svelte";
    import Edit from "carbon-icons-svelte/lib/Edit.svelte";
    import Download from "carbon-icons-svelte/lib/Download.svelte";
    import TrashCan from "carbon-icons-svelte/lib/TrashCan.svelte";
    import CaretLeft from "carbon-icons-svelte/lib/CaretLeft.svelte";
	import CaretRight from "carbon-icons-svelte/lib/CaretRight.svelte";
    import type { Repository } from "../libraries/repository";
    import type { RepositoryResponse } from "../model/repositoryResponse";
    import { DocumentType } from "../model/type";
    import { Utils } from "../utils/utils";
    import NotificationToast from "./NotificationToast.svelte";
    import { Constants } from "../utils/constants";
    import { navigate } from "svelte-navigator";
    import type { Indemnity } from "../model/indemnity";
    import { config } from "../config";
    import { LocalStorage } from "../libraries/localStorage";
    import dayjs from "dayjs";
    import { IndemnityStatus } from "../model/indemnityStatus";

    export let repository: Repository;
    let open: boolean = false;
    let indemnityUuuidToDelete: string;
    let promise: Promise<any>;
    let getIndemnitiesResponse: RepositoryResponse;
    let deleteIndemnityResponse: RepositoryResponse;
    let indemnitiesCount: number;
    let indemnitiesList: Array<Indemnity>;
    let orderByField: string = Constants.orderByField[0]; // default lastUpdate
    let orderByDirection: string = Constants.orderDirection[0]; // default desc
    let dateFilter =  null;
    let typeFilter = null;
    let clientFilter = null;
    let startDate = null;
    let endDate = null;
    let nasid = null;
    $: showPagination = true;
    $: showSearchError = false;
    $: showDownloadError = false;
    $: showDeleteError = false;
    $: showDeleteSuccess = false;
    $: showGetIndemnitiesError = false;
    $: offset = 0;
    $: size = "10";
    $: isLastPage = false;

    $: if(dateFilter != null && dateFilter != ""){ 
        if(getIndemnitiesResponse){
            indemnitiesList = getIndemnitiesResponse.data.indemnities.filter(i => Utils.dateFromTimestamp(i.lastUpdate) == dateFilter);
        }       
    }
    $: if(typeFilter != null && typeFilter != ""){ 
        if(getIndemnitiesResponse){
            indemnitiesList = getIndemnitiesResponse.data.indemnities.filter(i => i.type == typeFilter);
        }
    }
    $: if(clientFilter != null && clientFilter != ""){ 
        if(getIndemnitiesResponse){
            indemnitiesList = getIndemnitiesResponse.data.indemnities.filter(i => i.client == clientFilter);
        }
    }
    $: if(typeFilter != null && typeFilter != "" && dateFilter != null && dateFilter != ""){ 
        if(getIndemnitiesResponse){
            indemnitiesList = getIndemnitiesResponse.data.indemnities.filter(i => i.type == typeFilter && Utils.dateFromTimestamp(i.lastUpdate) == dateFilter);
        }
    }
    
    onMount(() => {
        promise = getIndemnities();
    })

    async function getIndemnities(){
        getIndemnitiesResponse = await repository.getIndemnities(offset, Number(size), orderByField, orderByDirection);
        if(getIndemnitiesResponse.success){
            indemnitiesCount = getIndemnitiesResponse.data.count;
            indemnitiesList = getIndemnitiesResponse.data.indemnities;
            if(indemnitiesList.length > 0 && indemnitiesList.length == Number(size)){
                isLastPage = false;
            }else{
                isLastPage = true;
            }
        }else{
            showGetIndemnitiesError = true;
        }
    }

    async function getActiveIndemnitiesInDatesRange(){
        if(startDate != null && endDate != null){
            showPagination = false;
            getIndemnitiesResponse = await repository.getActiveIndemnityInDatesRange(
                Utils.formatDate(startDate), 
                Utils.formatDate(endDate), 
                nasid);
            if(getIndemnitiesResponse.success){
                indemnitiesCount = getIndemnitiesResponse.data.count;
                indemnitiesList = getIndemnitiesResponse.data.indemnities; 
            }else{
                showGetIndemnitiesError = true;
            }
        }else{
            showSearchError = true;
        } 
    }

    async function deleteIndemnity(indemnityUuuidToDelete: string){
        let response: RepositoryResponse = await repository.deleteIndemnity(indemnityUuuidToDelete);
        if(response.success){
            showDeleteSuccess = true;
            promise = null;
            indemnitiesCount = null;
            indemnitiesList = null;
            promise = getIndemnities();
        }else{
            showDeleteError = true;
        }
    }

    async function downloadIndemnity(indemnityUuuid: string){
        let url = config.baseUrl + "indemnities/file/" + indemnityUuuid;
        let expire = LocalStorage.getSessionExpire();
        if(dayjs().isAfter(dayjs(expire))){
            repository.logOut();
        }else{
            let token: string = LocalStorage.getUserStorage().token;
            try{
            let response = await fetch(url, {
                    method: "GET",
                    headers: {
                        "Content-type" : "application/json",
                        "X-Api-Key" : token
                    }
                });
                if(response.ok){
                    let blob = await response.blob();
                    let blobUrl = window.URL.createObjectURL(blob);
                    let contentDisposition = response.headers.get("content-disposition");
                    let fileName = contentDisposition.match(/filename="(.+)"/)[1];
                    const a = document.createElement("a")
                    document.body.appendChild(a)
                    a.setAttribute("style", "display: none");
                    a.href = blobUrl;
                    a.download = fileName;
                    a.click()
                    window.URL.revokeObjectURL(blobUrl);
                }
            }catch(error){
                console.error(error);
                showDownloadError = true;
            }
        } 
    }

    function next(){
        offset = offset + Number(size);
        promise = getIndemnities();
    }

    function back(){
        offset = offset - Number(size);
        promise = getIndemnities();
    }

</script>

<!-- Modal to delete indemnity -->
<Modal 
  danger
  bind:open modalHeading="Eliminazione manleva"
  primaryButtonText="Conferma" 
  secondaryButtonText="Annulla"
  on:click:button--primary={() => { deleteIndemnity(indemnityUuuidToDelete); open = false; }}
  on:click:button--secondary={() => open = false}
  on:close={() => open = false}>
  Confermi di voler eliminare questa manleva?
</Modal>
<!-- end -->

<h2 class="mb-2">Manleve</h2>

<Accordion size="sm" align="start">
    <AccordionItem title="Ordina le manleve">
        <div class="base-flex">
            <Select size="sm" labelText="Campo sul quale ordinare" on:change={(e) => orderByField = Utils.translateToOrigin(e.detail.toString())}>
                {#each Constants.orderByField as field }
                    <SelectItem value={Utils.translateToIt(field)} />                  
                {/each}
            </Select>
            <Select size="sm" labelText="Direzione in cui ordinare" on:change={(e) => orderByDirection = Utils.translateToOrigin(e.detail.toString())}>
                {#each Constants.orderDirection as direction }
                    <SelectItem value={Utils.translateToIt(direction)} />                  
                {/each}
            </Select>
            <Button kind="tertiary" on:click={() => promise = getIndemnities()}>Ordina</Button>
        </div>
    </AccordionItem>
    <AccordionItem title="Cerca le manleve attive in un range temporale">
        <div class="base-flex">
            <DatePicker bind:value={startDate} dateFormat="d/m/Y" datePickerType="single" required>
                <DatePickerInput size="sm" labelText="Data inizio" />
            </DatePicker>
            <DatePicker bind:value={endDate} dateFormat="d/m/Y" datePickerType="single" required>
                <DatePickerInput size="sm" labelText="Data fine" />
            </DatePicker>
            <TextInput labelText="Nasid" size="sm" bind:value={nasid} required />
            <Button kind="tertiary" on:click={() => promise = getActiveIndemnitiesInDatesRange()}>Cerca</Button>
            <Button kind="tertiary" on:click={() => {
                getIndemnities(); 
                startDate = null; 
                endDate = null; 
                nasid = null;
                showPagination = true;
                }}>Reset</Button>
        </div>
    </AccordionItem>
    <AccordionItem title="Filtra le manleve ottenute">
        <div class="base-flex">
            <DatePicker bind:value={dateFilter} dateFormat="d/m/Y" datePickerType="single">
                <DatePickerInput size="sm" labelText="Filtra in base all'ultimo aggiornamento" />
            </DatePicker>
            <Select size="sm" labelText="Filtra in base alla tipologia" on:change={(e) => {
                    typeFilter = e.detail; 
                }}>
                <SelectItem value="" />
                {#each Object.keys(DocumentType) as key}
                    <SelectItem value={DocumentType[key]}></SelectItem>
                {/each}
            </Select>
            <TextInput labelText="Filtra in base al cliente" size="sm" bind:value={clientFilter} />
            <Button kind="tertiary" on:click={() => {
                indemnitiesList = getIndemnitiesResponse.data.indemnities; 
                dateFilter = null; 
                typeFilter = null; 
                clientFilter = null;
                }}>Reset</Button>
        </div>
    </AccordionItem>
</Accordion>

{#await promise}
    <StructuredListSkeleton rows={3}/>
{:then}
    {#if showGetIndemnitiesError}
        <div class="toast-notification">
            <NotificationToast title="Errore" subtitle={Utils.errorMessage(getIndemnitiesResponse)} kind="error" onClose={() => showGetIndemnitiesError = false} />
        </div>
    {/if}
    {#if getIndemnitiesResponse != null &&  getIndemnitiesResponse.data != null}
        {#if showDownloadError}
        <div class="toast-notification">
            <NotificationToast title="Errore" subtitle="Si è verificato un errore durante il download del file." kind="error" onClose={() => showDownloadError = false} />
        </div>
        {/if}  
        {#if showSearchError}
        <div class="toast-notification">
            <NotificationToast title="Errore" subtitle="I campi 'Data inizio' e 'Data fine' sono obbligatori." kind="error" onClose={() => showSearchError = false} />
        </div>
        {/if}  
        {#if showDeleteError}
        <div class="toast-notification">
            <NotificationToast title="Errore" subtitle={Utils.errorMessage(deleteIndemnityResponse)} kind="error" onClose={() => showDeleteError = false} />
        </div>
        {/if}    
        {#if showDeleteSuccess}
        <div class="toast-notification">
            <NotificationToast title="" subtitle="La manleva è stata eliminata con successo." kind="success" onClose={() => showDeleteSuccess = false} />
        </div>
        {/if}    
        <StructuredList class="mt-3" condensed>
            <StructuredListHead>
                <StructuredListRow head>
                    <StructuredListCell head>#</StructuredListCell> 
                    {#each Constants.indemnityHeaders as headerName, index}
                        <StructuredListCell head>{headerName}</StructuredListCell> 
                    {/each}
                </StructuredListRow>
            </StructuredListHead>
            <StructuredListBody>
                {#if indemnitiesCount === 0}
                <p class="mt-2 mb-2">Non sono presenti manleve.</p>
                {:else}
                {#each indemnitiesList as indemnity, index}
                    <StructuredListRow class={index%2 === 0 ? "even-row" : ""}>
                        <StructuredListCell>{index + 1}</StructuredListCell>
                        <StructuredListCell>{Utils.returnValueOrAlternativeValue(indemnity.description, "-")}</StructuredListCell>
                        <StructuredListCell>{Utils.returnValueOrAlternativeValue(indemnity.notes, "-")}</StructuredListCell>
                        <StructuredListCell>{indemnity.type}</StructuredListCell>     
                        <StructuredListCell>{indemnity.client}</StructuredListCell>  
                        <StructuredListCell>{indemnity.nasid}</StructuredListCell>            
                        <StructuredListCell>{indemnity.since ? Utils.dateFromTimestamp(Number(indemnity.since)) : "-"}</StructuredListCell>
                        <StructuredListCell>{indemnity.until ? Utils.dateFromTimestamp(Number(indemnity.until)) : "-"}</StructuredListCell> 
                        <StructuredListCell>{indemnity.indemnityStatus}</StructuredListCell>
                        <StructuredListCell>{Utils.returnValueOrAlternativeValue(indemnity.operator, "-")}</StructuredListCell>
                        <StructuredListCell>{Utils.returnValueOrAlternativeValue(Utils.dateFromTimestamp(indemnity.lastUpdate), "-")}</StructuredListCell>
                        <StructuredListCell>
                            <Button size="small" kind="ghost" iconDescription="Scarica" icon={Download} on:click={() => { downloadIndemnity(indemnity.uuid); }}/>
                            <Button disabled={indemnity.indemnityStatus === IndemnityStatus.PUBLISHED} size="small" kind="ghost" iconDescription="Modifica" icon={Edit} on:click={() => { navigate("/indemnities/update/" + indemnity.uuid)}}/>
                            <Button disabled={indemnity.indemnityStatus === IndemnityStatus.PUBLISHED} size="small" kind="ghost" iconDescription="Elimina" icon={TrashCan} on:click={() => { open = true; indemnityUuuidToDelete = indemnity.uuid }}/>
                        </StructuredListCell>
                    </StructuredListRow>                    
                {/each}               
                {/if}
            </StructuredListBody>
        </StructuredList>
        <!-- Pagination -->
        {#if showPagination}
        <div class="bx--pagination">
            <div class="bx--pagination__left">
                <label id="select-id-pagination-count-label" class="bx--pagination__text" for="select-id-pagination-count">Manleve per pagina:</label>
                <Select class="bx--select bx--select--inline bx--select__item-count" bind:selected={size} on:change={() => {offset = 0; getIndemnities()}}>
                    <SelectItem value="5" />
                    <SelectItem value="10" />
                    <SelectItem value="30" />
                    <SelectItem value="50" />
                </Select>
                <span class="bx--pagination__text bx--pagination__items-count">{offset + 1 } - {offset + indemnitiesList.length} di {indemnitiesCount}</span>
            </div>
            <div class="bx--pagination__right">
                <div class="bx--pagination__control-buttons">
                    <Button 
                        disabled={offset === 0}
                        class="bx--pagination__button bx--pagination__button--backward bx--btn bx--btn--ghost bx--tooltip--hidden bx--btn--icon-only bx--tooltip__trigger bx--tooltip--a11y bx--btn--icon-only--top bx--tooltip--align-center" 
                        icon={CaretLeft}
                        iconDescription="Indietro"
                        on:click={back}
                    />
                    <Button 
                        disabled={isLastPage}
                        class="bx--pagination__button bx--pagination__button--forward bx--btn bx--btn--ghost bx--tooltip--hidden bx--btn--icon-only bx--tooltip__trigger bx--tooltip--a11y bx--btn--icon-only--top bx--tooltip--align-end" 
                        icon={CaretRight} 
                        iconDescription="Avanti"
                        on:click={next}
                    />
                </div>
            </div>
        </div>
        {/if}
    {:else}
        <p style="margin-top: 1.5rem;">Nessun dato</p>
    {/if}

{/await}